import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Convert any YouTube URL format to an embed URL.
 * Supports: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
 */
function toYouTubeEmbedUrl(url: string): string {
  if (!url) return url;

  let videoId: string | null = null;

  if (url.includes("/embed/")) {
    const match = url.match(/\/embed\/([a-zA-Z0-9_-]+)/);
    videoId = match ? match[1] : null;
  } else {
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    const watchMatch = url.match(/youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/);
    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
    videoId = shortMatch?.[1] ?? watchMatch?.[1] ?? shortsMatch?.[1] ?? null;
  }

  if (videoId) return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}`;

  return url;
}

const PAUSE_EVENT = "videoplayer:pause-others";

export const VideoPlayer = ({ url }) => {
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const idRef = useRef(Math.random().toString(36).slice(2));

  const handleLoading = useCallback(() => {
    setLoading(false);
    // Listen for state changes from this iframe
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "listening", id: idRef.current }),
      "*"
    );
  }, []);

  const pauseVideo = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
      "*"
    );
  }, []);

  // Listen for pause requests from other players
  useEffect(() => {
    const handlePause = (e: CustomEvent) => {
      if (e.detail !== idRef.current) pauseVideo();
    };
    window.addEventListener(PAUSE_EVENT, handlePause as EventListener);
    return () => window.removeEventListener(PAUSE_EVENT, handlePause as EventListener);
  }, [pauseVideo]);

  // Detect when user clicks this iframe (focus-based detection)
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const checkFocus = () => {
      if (document.activeElement === iframeRef.current) {
        window.dispatchEvent(new CustomEvent(PAUSE_EVENT, { detail: idRef.current }));
        // Blur so we can detect the next click
        if (iframeRef.current) {
          iframeRef.current.blur();
          window.focus();
        }
      }
    };

    intervalId = setInterval(checkFocus, 300);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!url) {
    return <></>;
  }

  const embedUrl = toYouTubeEmbedUrl(url);

  return (
    <Box
      style={{
        borderRadius: "20px",
        border: loading ? "4px solid inherit" : "4px solid #000",
        overflow: "hidden",
        verticalAlign: "middle"
      }}
      sx={{
        position: "relative",
        m: "auto",
        width: "100%",
        height: "100%"
      }}
    >
      <iframe
        ref={iframeRef}
        title="Youtube video"
        style={{
          width: "100%",
          height: "100%",
          border: "none"
        }}
        src={embedUrl}
        frameBorder="0"
        loading="lazy"
        onLoad={handleLoading}
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper"
          }}
        >
          <CircularProgress size="3rem" color="primary" />
        </Box>
      )}
    </Box>
  );
};
